import React, {
    useContext,
    useState,
    useEffect,
    useCallback
} from 'react';
import _, { isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { AccountService } from 'gw-capability-gateway-policy';
import metadata from './MLEditContactDetailsComponent.metadata.json5';
import styles from './MLEditContactDetailsComponent.module.scss';
import './MLEditContactDetailsComponent.messages';

const homePrimaryPhoneTypeCode = 'home';
const workPrimaryPhoneTypeCode = 'work';
const mobilePrimaryPhoneTypeCode = 'mobile';
const primaryPhoneTypeValPath = 'primaryPhoneType.value';

function MLEditContactDetailsComponent(props) {
    const {
        accountContactDetailsVm,
        updateContactVM,
        showValidationErrors,
        isAgent,
        showEmailScript,
        showPrimaryEmailAddress,
        showSpouseEmailAddress,
        writeValue,
        path,
        onValidate,
        id,
        isSkipping,
        canEditCommFields,
        originalContactDetails,
        newPrimaryNamedInsuredClicked,
        setNewPrimaryNamedInsuredClicked,
        emailAddressBlurred,
        validateEmailAddress
    } = props;
    const {
        onValidate: setComponentValidation,
        isComponentValid
    } = useValidation(id);

    const disableContactField = (value) => {
        return (isAgent && !canEditCommFields && value);
    };

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const viewModelService = useContext(ViewModelServiceContext);

    const isPhoneNumberValid = useCallback((phoneType) => {
        const isValid = _.get(accountContactDetailsVm, `${phoneType}Number.value`)
            && _.get(accountContactDetailsVm, `${phoneType}Number.aspects.valid`)
            && _.get(accountContactDetailsVm, `${phoneType}Number.value.length`) === 10;
        return !!isValid;
    }, [accountContactDetailsVm]);

    const [isHomePhonePrimary, updateIsHomePhonePrimary] = useState(false);
    const [isWorkPhonePrimary, updateIsWorkPhonePrimary] = useState(false);
    const [isCellPhonePrimary, updateIsCellPhonePrimary] = useState(false);

    const [addtlPhoneNumbersVisible, updateAddtlPhoneNumbersVisible] = useState(false);

    const { authUserData } = useAuthentication();
    const { MLUserTypes } = appConfig;

    let isEAorIA = false;
    if (authUserData) {
        isEAorIA = authUserData.userType === MLUserTypes.PCS || authUserData.userType === MLUserTypes.IA;
    }

    const changePrimaryPhone = useCallback((primaryPhoneType, checked) => {
        let selectedPrimaryPhoneTypeCode;
        if (!checked) {
            selectedPrimaryPhoneTypeCode = undefined;
            _.set(accountContactDetailsVm, primaryPhoneTypeValPath, undefined);
        } else {
            const primaryPhoneTypes = viewModelService.productMetadata.get('pc').types.getTypelist('PrimaryPhoneType');
            const selectedPrimaryPhoneType = primaryPhoneTypes.getCode(primaryPhoneType);
            _.set(accountContactDetailsVm, primaryPhoneTypeValPath, selectedPrimaryPhoneType);

            selectedPrimaryPhoneTypeCode = selectedPrimaryPhoneType.code;
        }
        updateIsHomePhonePrimary(selectedPrimaryPhoneTypeCode === homePrimaryPhoneTypeCode);
        updateIsWorkPhonePrimary(selectedPrimaryPhoneTypeCode === workPrimaryPhoneTypeCode);
        updateIsCellPhonePrimary(selectedPrimaryPhoneTypeCode === mobilePrimaryPhoneTypeCode);
    }, [accountContactDetailsVm, viewModelService.productMetadata]);


    const updatePhoneNumber = useCallback((phoneType, value, changedPath) => {
        if (changedPath) {
            if (value) {
                const unmaskedPhoneNumber = value.replace(/[^\d]/g, '');
                _.set(accountContactDetailsVm, `${changedPath}.value`, unmaskedPhoneNumber);
            } else {
                _.set(accountContactDetailsVm, `${changedPath}.value`, undefined);
            }
        }

        switch (phoneType) {
            case 'home':
                if (_.get(accountContactDetailsVm, 'homeNumber.value') === '') {
                    _.set(accountContactDetailsVm, 'homeNumber.value', undefined);
                }
                break;
            case 'cell':
                if (_.get(accountContactDetailsVm, 'cellNumber.value') === '') {
                    _.set(accountContactDetailsVm, 'cellNumber.value', undefined);
                }
                break;
            case 'work':
                if (_.get(accountContactDetailsVm, 'workNumber.value') === '') {
                    _.set(accountContactDetailsVm, 'workNumber.value', undefined);
                }
                break;
            default:
                break;
        }
        updateContactVM(accountContactDetailsVm);
    }, [accountContactDetailsVm, updateContactVM]);

    useEffect(() => {
        if (!isSkipping) {
            const primaryPhoneType = _.get(accountContactDetailsVm, primaryPhoneTypeValPath);
            if (primaryPhoneType !== undefined) {
                if (_.get(accountContactDetailsVm, 'cellNumber.value') || _.get(accountContactDetailsVm, 'workNumber.value')) {
                    updateAddtlPhoneNumbersVisible(true);
                }
                changePrimaryPhone(_.get(accountContactDetailsVm, primaryPhoneTypeValPath).code, true);
            } else {
                const isAccountHolder = _.get(accountContactDetailsVm, 'accountHolder.value', false);
                changePrimaryPhone(isAccountHolder ? undefined : homePrimaryPhoneTypeCode, !isAccountHolder);
            }
        }
    }, [accountContactDetailsVm, accountContactDetailsVm.value, changePrimaryPhone]);

    useEffect(() => {
        if (newPrimaryNamedInsuredClicked) {
            updateIsHomePhonePrimary(false);
            updateAddtlPhoneNumbersVisible(false);
            setNewPrimaryNamedInsuredClicked(false);
        }
    }, [newPrimaryNamedInsuredClicked, setNewPrimaryNamedInsuredClicked]);

    const onBlurEmailAddress = useCallback(() => {
        validateEmailAddress(accountContactDetailsVm);
        emailAddressBlurred()
    }, [emailAddressBlurred,validateEmailAddress]);

    const showAdditionalPhoneNumbers = () => {
        updateAddtlPhoneNumbersVisible(true);
    };

    const onValueChange = (value, changedPath) => {
        writeValue(value, `${path}.${changedPath}`);
    };

    const emailTrim = ( event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1;
        }
    }

    const overrideProps = {
        homePhonePrimaryCheckbox: {
            value: isHomePhonePrimary,
            onValueChange: (value) => {
                changePrimaryPhone(homePrimaryPhoneTypeCode, value);
                updateContactVM(accountContactDetailsVm);
            }
        },
        homePhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('home', value, changedPath),
            disabled: disableContactField((originalContactDetails) ? originalContactDetails.originalHomeNumber : null)
        },
        additionalPhoneNumberLinkContainer: {
            visible: !addtlPhoneNumbersVisible
        },
        additionalPhoneNumbersLink: {
            onClick: showAdditionalPhoneNumbers
        },
        additionalPhoneNumbersContainer: {
            visible: addtlPhoneNumbersVisible
        },
        workPhonePrimaryCheckbox: {
            value: isWorkPhonePrimary,
            onValueChange: (value) => {
                changePrimaryPhone(workPrimaryPhoneTypeCode, value);
                updateContactVM(accountContactDetailsVm);
            }
        },
        workPhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('work', value, changedPath),
            disabled: disableContactField((originalContactDetails) ? originalContactDetails.originalWorkNumber : null)
        },
        cellPhonePrimaryCheckbox: {
            value: isCellPhonePrimary,
            onValueChange: (value) => {
                changePrimaryPhone(mobilePrimaryPhoneTypeCode, value);
                updateContactVM(accountContactDetailsVm);
            }
        },
        cellPhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('cell', value, changedPath),
            disabled: disableContactField((originalContactDetails) ? originalContactDetails.originalCellNumber : null)
        },
        primaryEmailAddressContainer: {
            visible: showPrimaryEmailAddress
        },
        emailAddress: {
            onBlur: onBlurEmailAddress,
            onFocus: ( event ) => emailTrim(event),
            disabled: disableContactField((originalContactDetails) ? originalContactDetails.originalEmailAddress : null)
        },
        updatesOnEmailAddress: {
            visible: !isAgent
        },
        emailUpdateLink: {
            visible: !isAgent,
            // href: MLLinks.metlifeOnline, //todo work for not agent
            target: '_blank'
        },
        spouseEmailAddressContainer: {
            visible: showSpouseEmailAddress
        },
        emailScriptContainer: {
            visible: showEmailScript
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={accountContactDetailsVm}
            overrideProps={overrideProps}
            showErrors={showValidationErrors}
            onValueChange={onValueChange}
            onValidationChange={setComponentValidation}
            classNameMap={styles}
        />
    );
}

MLEditContactDetailsComponent.propTypes = {
    accountContactDetailsVm: PropTypes.shape({}).isRequired,
    updateContactVM: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    isAgent: PropTypes.bool,
    showEmailScript: PropTypes.bool,
    showPrimaryEmailAddress: PropTypes.bool,
    writeValue: PropTypes.func.isRequired,
    showSpouseEmailAddress: PropTypes.bool,
    path: PropTypes.string.isRequired,
    isSkipping: PropTypes.bool,
    emailAddressBlurred: PropTypes.func
};

MLEditContactDetailsComponent.defaultProps = {
    isAgent: false,
    showEmailScript: false,
    showPrimaryEmailAddress: true,
    showSpouseEmailAddress: false,
    isSkipping: false
};

export default MLEditContactDetailsComponent;
